import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icons } from '@ohif/ui-next';
import { Button } from '../Button';
import { useTranslation } from 'react-i18next';

export enum showDialogOption {
  NeverShowDialog = 'never',
  AlwaysShowDialog = 'always',
  ShowOnceAndConfigure = 'configure',
}

const InvestigationalUseDialog = ({
  dialogConfiguration = {
    option: showDialogOption.AlwaysShowDialog,
  },
}) => {
  const { option, days } = dialogConfiguration;
  const [isHidden, setIsHidden] = useState(true);
  const { t } = useTranslation('InvestigationalUseDialog');

  useEffect(() => {
    const dialogLocalState = localStorage.getItem('investigationalUseDialog');
    const dialogSessionState = sessionStorage.getItem('investigationalUseDialog');

    switch (option) {
      case showDialogOption.NeverShowDialog:
        setIsHidden(true);
        break;
      case showDialogOption.AlwaysShowDialog:
        setIsHidden(!!dialogSessionState);
        break;
      case showDialogOption.ShowOnceAndConfigure:
        if (dialogLocalState) {
          const { expiryDate } = JSON.parse(dialogLocalState);
          const isExpired = new Date() > new Date(expiryDate);
          setIsHidden(!isExpired);
        } else {
          setIsHidden(false);
        }
        break;
      default:
        setIsHidden(true);
    }
  }, [option, days]);

  const handleConfirmAndHide = () => {
    const expiryDate = new Date();

    switch (option) {
      case showDialogOption.ShowOnceAndConfigure:
        expiryDate.setDate(expiryDate.getDate() + days);
        localStorage.setItem('investigationalUseDialog', JSON.stringify({ expiryDate }));
        break;
      case showDialogOption.AlwaysShowDialog:
        sessionStorage.setItem('investigationalUseDialog', 'hidden');
        break;
    }
    setIsHidden(true);
  };

  if (isHidden) {
    return null;
  }

  return (
    // `data-t2v-keep-clear` is how the chat launcher finds this: it rides above
    // whatever matches that selector while it is on screen, and drops back to
    // the corner once it is dismissed.
    <div data-t2v-keep-clear className="fixed bottom-2 z-[60] flex w-full justify-center">
      <div className="bg-secondary-dark border-primary-dark flex w-[90%] flex-col items-center gap-3 rounded-lg border-2 px-4 py-3 shadow-lg sm:flex-row sm:justify-between sm:gap-4 sm:px-[22px]">
        <div className="flex items-center gap-3 sm:gap-4">
          <Icons.Talk2View className="h-10 w-10 flex-shrink-0 sm:h-12 sm:w-12" />
          <div className="flex flex-col">
            <div className="text-[15px] leading-tight text-primary-light sm:text-[19px]">
              <span className="text-white">Talk2View</span> is{' '}
              {t('for investigational use only')}
            </div>
            <div className="text-[12px] text-white sm:text-[13px]">
              <span
                className="text-primary-active cursor-pointer"
                onClick={() => window.open('https://www.talk2view.com/', '_blank')}
              >
                {t('Learn more about Talk2View')}
              </span>
            </div>
          </div>
        </div>
        <Button
          onClick={handleConfirmAndHide}
          className="bg-primary-active hover:bg-primary-active/80 w-full flex-shrink-0 px-6 py-2 text-sm font-semibold text-black shadow-md sm:w-auto"
          dataCY="confirm-and-hide-button"
        >
          {t('Confirm and hide')}
        </Button>
      </div>
    </div>
  );
};

InvestigationalUseDialog.propTypes = {
  dialogConfiguration: PropTypes.shape({
    option: PropTypes.oneOf(Object.values(showDialogOption)).isRequired,
    days: PropTypes.number,
  }),
};

export default InvestigationalUseDialog;
